import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ComponentType,
} from 'react';
import { Skeleton, Stack } from '@mui/material';
import ConfettiExplosion from 'react-confetti-explosion';
import {
  ExperienceBuilder,
  ExperienceBuilderSkeleton,
} from './components/experience-builder';
import { PageContainer } from '@/components/shared/page-container';
import { useRecommendationProfile } from '@/context/recommendation-profile';
import { usePathsIndex } from '@/data/learning-path/path';
import { useUser } from '@/data/user';
import { useBootstrapData } from '@/data/user/bootstrap-data';
import { Features } from '@/models/bootstrap/plan';
import { Section, type SkeletonType } from '@/packages/home/components/section';
import {
  IaBuilderProvider,
  useIaBuildContext,
} from '@/packages/home/context/ia-builder';
import {
  homeSections,
  type SectionConfig,
} from '@/packages/home/home-sections';
import {
  getCourseTypesWithLink,
  getKnowledgeAreasWithLink,
  getUniversityCard,
} from '@/packages/home/utils/course-list-utils';
import { hasFeature } from '@/utils/user-permissions';

interface Section {
  id: number;
  title: string;
  description: string;
  tag: ComponentType;
  skeletonType: SkeletonType;
  skipIaBuilder: boolean;
  content: ComponentType;
}

interface SectionsContentProps {
  showConfetti: boolean;
  iaBuilderActive: boolean;
  sections: SectionConfig[];
  handleExperienceComplete: () => void;
}

const SectionsContent = ({
  showConfetti,
  iaBuilderActive,
  sections,
  handleExperienceComplete,
}: SectionsContentProps) => {
  const { nextSection } = useIaBuildContext();

  return (
    <>
      {showConfetti && (
        <Stack position="fixed" top={0} left="50%" alignItems="center">
          <ConfettiExplosion zIndex={2000} />
        </Stack>
      )}
      {iaBuilderActive && (
        <ExperienceBuilder
          sections={sections}
          onComplete={handleExperienceComplete}
        />
      )}
      {sections.map((section) => {
        return (
          <Section
            key={section.id}
            id={section.id}
            title={section.title}
            description={section.description}
            tag={section.tag}
            skeletonType={section.skeletonType as SkeletonType}
            skipIaBuilder={section.skipIaBuilder}
            onContentComplete={!section.skipIaBuilder ? nextSection : undefined}
          >
            {section.content}
          </Section>
        );
      })}
    </>
  );
};

export const Home = () => {
  const { bootstrap } = useBootstrapData();
  const { user } = useUser();
  const { hasCompanyPaths } = usePathsIndex();
  const { iaBuilderActive, setIaBuilderActive } = useRecommendationProfile();
  const [isLoadingExperienceBuilder, setIsLoadingExperienceBuilder] =
    useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const courseTypes = bootstrap?.taxonomy?.course_types || [];
  const knowledgeAreas = bootstrap?.taxonomy?.knowledge_areas || [];
  const universityCard = getUniversityCard(user);
  const customCategory = bootstrap?.info?.colaborador?.empresa?.custom_category;
  const courseTypesWithLink = getCourseTypesWithLink({
    plan: bootstrap?.plan,
    courseTypes,
    universityCard,
    customCategory,
    info: bootstrap?.info,
  });
  const knowledgeAreasWithLink = getKnowledgeAreasWithLink(knowledgeAreas);
  const einsteinCobrand = bootstrap?.plan?.features?.some(
    (el) => el.name === Features.Einstein
  );
  const learningPaths = bootstrap?.plan?.features?.some(
    (el) => el.name === Features.LearningPaths
  );
  const idp = bootstrap?.plan?.features?.some((el) => el.name === Features.Idp);
  const companyName = bootstrap?.info?.colaborador?.empresa?.empresa || '';
  const agentVideoRecommendation = bootstrap && hasFeature(
    bootstrap.plan,
    Features.DailyInsight
  );

  const sections = useMemo(
    () =>
      homeSections({
        knowledgeAreas: knowledgeAreasWithLink,
        courseTypes: courseTypesWithLink,
        idp,
        einsteinCobrand,
        learningPaths,
        hasCompanyPaths,
        companyName,
        agentVideoRecommendation,
      }),
    [
      knowledgeAreasWithLink,
      courseTypesWithLink,
      idp,
      einsteinCobrand,
      learningPaths,
      hasCompanyPaths,
      companyName,
      agentVideoRecommendation,
    ]
  );

  const handleExperienceComplete = useCallback(() => {
    setShowConfetti(true);
    setIaBuilderActive(false);
  }, [setIaBuilderActive]);

  useEffect(() => {
    if (!iaBuilderActive) return;

    setIsLoadingExperienceBuilder(true);

    // Quando o iaBuilder é ativado após terminar o quiz, precisamos mostrar skeleton por 1s para simular a experiência de construção da IA
    const timer = setTimeout(() => {
      setIsLoadingExperienceBuilder(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [iaBuilderActive]);

  if (isLoadingExperienceBuilder && iaBuilderActive)
    return (
      <>
        <ExperienceBuilderSkeleton />
        <Skeleton />
      </>
    );

  return (
    <PageContainer>
      <IaBuilderProvider sections={sections} showAllSections={!iaBuilderActive}>
        <Stack gap={{ xs: 4, md: 7 }} position="relative">
          <SectionsContent
            showConfetti={showConfetti}
            iaBuilderActive={iaBuilderActive}
            sections={sections}
            handleExperienceComplete={handleExperienceComplete}
          />
        </Stack>
      </IaBuilderProvider>
    </PageContainer>
  );
};
